import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import DesyncAlertCard from '../../components/admin/DesyncAlertCard';
import FeriaAccessCard from '../../components/admin/FeriaAccessCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Users, ClipboardList, Dumbbell, Plus, ArrowRight, RefreshCw, CheckCircle2, Loader2, Heart, Lightbulb, RotateCcw, HeartPulse, AlertTriangle, ChevronRight, Stethoscope, ShieldCheck, ShieldAlert, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_clients: 0, total_plans: 0, total_exercises: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [endoAlerts, setEndoAlerts] = useState(null);
  const [loadingEndo, setLoadingEndo] = useState(true);
  const [dataHealth, setDataHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchEndometriosisAlerts();
    fetchDataHealth();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEndometriosisAlerts = async () => {
    try {
      const response = await axios.get(`${API}/admin/endometriosis-alerts-dashboard`);
      setEndoAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch endometriosis alerts:', error);
    } finally {
      setLoadingEndo(false);
    }
  };

  const fetchDataHealth = async () => {
    try {
      setLoadingHealth(true);
      const response = await axios.get(`${API}/admin/data-health-dashboard`);
      setDataHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch data health:', error);
      // Set a default value to show the panel even on error
      setDataHealth({
        summary: {
          total_clients_with_plans: 0,
          healthy: 0,
          issues_found: 0,
          no_progress_yet: 0,
          health_score: 100
        },
        issues: [],
        healthy_clients: [],
        clients_without_progress: [],
        error: 'Error al cargar datos'
      });
    } finally {
      setLoadingHealth(false);
    }
  };

  const syncExercises = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/sync-exercises`);
      toast.success(`✅ ${response.data.message}`);
      setSyncDone(true);
    } catch (error) {
      toast.error('Error al sincronizar ejercicios');
      console.error(error);
    } finally {
      setSyncing(false);
    }
  };

  const [creatingSenior, setCreatingSenior] = useState(false);
  const [seniorCreated, setSeniorCreated] = useState(false);
  const [resettingClients, setResettingClients] = useState(false);

  const createSeniorPlan = async () => {
    setCreatingSenior(true);
    try {
      const response = await axios.post(`${API}/admin/create-senior-plan`);
      toast.success(`✅ ${response.data.message}`);
      setSeniorCreated(true);
      fetchStats(); // Refresh stats
    } catch (error) {
      toast.error('Error al crear plan Senior');
      console.error(error);
    } finally {
      setCreatingSenior(false);
    }
  };

  const resetAllClientsProgress = async () => {
    if (!window.confirm('⚠️ ¿Estás seguro de resetear el progreso de TODOS los clientes?\n\nTodos los clientes volverán a ver la Sesión 1 de su plan asignado.')) {
      return;
    }
    setResettingClients(true);
    try {
      const response = await axios.post(`${API}/admin/reset-all-clients`);
      toast.success(`✅ ${response.data.message} - Todos verán la Sesión 1`);
    } catch (error) {
      toast.error('Error al resetear clientes');
      console.error(error);
    } finally {
      setResettingClients(false);
    }
  };

  const statCards = [
    { 
      title: t('totalClients'), 
      value: stats.total_clients, 
      icon: Users, 
      link: '/admin/clients',
      color: 'text-primary'
    },
    { 
      title: t('totalPlans'), 
      value: stats.total_plans, 
      icon: ClipboardList, 
      link: '/admin/plans',
      color: 'text-emerald-500'
    },
    { 
      title: t('totalExercises'), 
      value: stats.total_exercises, 
      icon: Dumbbell, 
      link: '/admin/exercises',
      color: 'text-amber-500'
    },
  ];

  const quickActions = [
    { label: t('newClient'), link: '/admin/clients', icon: Users },
    { label: t('newPlan'), link: '/admin/plans', icon: ClipboardList },
    { label: t('newExercise'), link: '/admin/exercises', icon: Dumbbell },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {t('welcomeBack')}, {user?.name}
          </h1>
          <p className="text-muted-foreground mt-1">{t('dashboard')}</p>
        </div>

        {/* Acceso rápido a las 3 pantallas de la Feria Granada */}
        <FeriaAccessCard />

        {/* Alerta automática de clientes desincronizados (salto de mesociclo) */}
        <DesyncAlertCard basePath="/admin/clients" />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-children">
          {statCards.map((stat) => (
            <Link key={stat.title} to={stat.link}>
              <Card className="bg-card border-border card-hover cursor-pointer" data-testid={`stat-card-${stat.title}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.title}</p>
                      <p className="text-4xl font-bold text-foreground mt-2 tabular-nums">
                        {loading ? '-' : stat.value}
                      </p>
                    </div>
                    <div className={`p-3 rounded-sm bg-white/5 ${stat.color}`}>
                      <stat.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Export Section */}
        <Card className="bg-card border-border" data-testid="export-section">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Download className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Exportar Datos</h3>
                  <p className="text-xs text-muted-foreground">Clientes, planes, sesiones y ejercicios</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-green-600/50 text-green-400 hover:bg-green-600/20"
                  onClick={async () => {
                    try {
                      toast.info('Generando Excel...');
                      const res = await axios.get(`${API}/admin/export/all?format=excel`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `GuardiansFit_Export_${new Date().toISOString().slice(0,10)}.xlsx`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Excel descargado');
                    } catch (e) {
                      toast.error('Error al exportar');
                    }
                  }}
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-600/50 text-red-400 hover:bg-red-600/20"
                  onClick={async () => {
                    try {
                      toast.info('Generando PDF...');
                      const res = await axios.get(`${API}/admin/export/all?format=pdf`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `GuardiansFit_Export_${new Date().toISOString().slice(0,10)}.pdf`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('PDF descargado');
                    } catch (e) {
                      toast.error('Error al exportar');
                    }
                  }}
                  data-testid="export-pdf-btn"
                >
                  <FileText className="h-4 w-4 mr-1.5" />
                  PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Video Converter */}
        <Card className="bg-card border-border" data-testid="video-converter-section">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <RefreshCw className="h-5 w-5 text-orange-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Sincronizar Videos</h3>
                  <p className="text-xs text-muted-foreground">Convierte videos iPhone a H.264 + propaga a todos los planes</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-orange-600/50 text-orange-400 hover:bg-orange-600/20"
                onClick={async () => {
                  toast.info('Paso 1/2: Convirtiendo videos...');
                  try {
                    const conv = await axios.post(`${API}/admin/convert-all-videos`);
                    const c = conv.data;
                    toast.success(`Convertidos: ${c.converted?.length || 0} | Ya OK: ${c.skipped?.length || 0} | Errores: ${c.errors?.length || 0}`);
                  } catch (e) { toast.error('Error al convertir'); }
                  
                  toast.info('Paso 2/2: Propagando a planes...');
                  try {
                    const prop = await axios.post(`${API}/admin/propagate-videos-to-plans`);
                    toast.success(prop.data.message || 'Videos sincronizados');
                  } catch (e) { toast.error('Error al propagar'); }
                }}
                data-testid="sync-videos-btn"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Sincronizar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Endometriosis Alerts Section */}
        {!loadingEndo && endoAlerts && endoAlerts.total_clients > 0 && (
          <Card className={`border ${endoAlerts.critical_alerts > 0 ? 'border-red-500/50 bg-red-500/5' : 'border-pink-500/30 bg-pink-500/5'}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <HeartPulse className={`h-5 w-5 ${endoAlerts.critical_alerts > 0 ? 'text-red-400' : 'text-pink-400'}`} />
                  Seguimiento Endometriosis
                  {endoAlerts.critical_alerts > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {endoAlerts.critical_alerts} alerta{endoAlerts.critical_alerts > 1 ? 's' : ''} crítica{endoAlerts.critical_alerts > 1 ? 's' : ''}
                    </Badge>
                  )}
                </CardTitle>
                <Link to="/admin/clients">
                  <Button variant="ghost" size="sm" className="text-pink-400 hover:text-pink-300">
                    Ver todas <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-pink-300">{endoAlerts.total_clients}</p>
                  <p className="text-xs text-muted-foreground">Clientas</p>
                </div>
                <div>
                  <p className={`text-2xl font-bold ${endoAlerts.critical_alerts > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {endoAlerts.critical_alerts}
                  </p>
                  <p className="text-xs text-muted-foreground">Alertas críticas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">{endoAlerts.clients_with_alerts}</p>
                  <p className="text-xs text-muted-foreground">Requieren atención</p>
                </div>
              </div>
              
              {/* Lista de clientas con alertas críticas */}
              {endoAlerts.clients && endoAlerts.clients.filter(c => c.has_critical_alert).length > 0 && (
                <div className="space-y-2 mt-4">
                  <p className="text-xs font-medium text-red-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Requieren atención inmediata:
                  </p>
                  {endoAlerts.clients.filter(c => c.has_critical_alert).slice(0, 3).map((client) => (
                    <Link key={client.client_id} to={`/admin/clients/${client.client_id}`}>
                      <div className="flex items-center justify-between p-2 rounded bg-red-500/10 hover:bg-red-500/20 transition-colors">
                        <div>
                          <p className="text-sm font-medium text-foreground">{client.client_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Dolor post: {client.metrics.avg_pain_post}/10 · Energía: {client.metrics.avg_energy}/5
                          </p>
                        </div>
                        <div className="flex gap-1">
                          {client.alerts.slice(0, 2).map((alert, idx) => (
                            <Badge 
                              key={idx}
                              variant="outline" 
                              className={`text-xs ${
                                alert.type === 'danger' ? 'border-red-500/50 text-red-400' : 'border-amber-500/50 text-amber-400'
                              }`}
                            >
                              {alert.icon}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Data Health Dashboard - Panel de Salud de Datos */}
        <Card className={`border ${
          loadingHealth ? 'border-zinc-700' :
          dataHealth?.summary?.issues_found > 0 
            ? 'border-amber-500/50 bg-amber-500/5' 
            : 'border-emerald-500/30 bg-emerald-500/5'
        }`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                {loadingHealth ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : dataHealth?.summary?.issues_found > 0 ? (
                  <ShieldAlert className="h-5 w-5 text-amber-400" />
                ) : (
                  <ShieldCheck className="h-5 w-5 text-emerald-400" />
                )}
                Salud de Datos
                {!loadingHealth && dataHealth?.summary?.issues_found > 0 && (
                  <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-400">
                    {dataHealth.summary.issues_found} problema{dataHealth.summary.issues_found > 1 ? 's' : ''}
                  </Badge>
                )}
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-muted-foreground hover:text-foreground"
                onClick={fetchDataHealth}
                disabled={loadingHealth}
              >
                <RefreshCw className={`h-4 w-4 ${loadingHealth ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingHealth ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : dataHealth ? (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-4 mb-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{dataHealth.summary?.total_clients_with_plans || 0}</p>
                    <p className="text-xs text-muted-foreground">Clientes con plan</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-400">{dataHealth.summary?.healthy || 0}</p>
                    <p className="text-xs text-muted-foreground">Sincronizados</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${dataHealth.summary?.issues_found > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {dataHealth.summary?.issues_found || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Con problemas</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${
                      dataHealth.summary?.health_score >= 90 ? 'text-emerald-400' :
                      dataHealth.summary?.health_score >= 70 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {dataHealth.summary?.health_score || 100}%
                    </p>
                    <p className="text-xs text-muted-foreground">Score salud</p>
                  </div>
                </div>

                {/* Error message if any */}
                {dataHealth.error && (
                  <p className="text-xs text-red-400 text-center mb-2">{dataHealth.error}</p>
                )}

                {/* Issues List */}
                {dataHealth.issues && dataHealth.issues.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <p className="text-xs font-medium text-amber-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Clientes con desincronización de mesociclo:
                    </p>
                  {dataHealth.issues.slice(0, 5).map((issue) => (
                    <Link key={issue.client_id} to={`/admin/clients/${issue.client_id}`}>
                      <div className={`flex items-center justify-between p-2 rounded transition-colors ${
                        issue.severity === 'HIGH' 
                          ? 'bg-red-500/10 hover:bg-red-500/20' 
                          : 'bg-amber-500/10 hover:bg-amber-500/20'
                      }`}>
                        <div>
                          <p className="text-sm font-medium text-foreground">{issue.client_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {issue.description} · {issue.sessions_completed} sesiones
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              issue.severity === 'HIGH' 
                                ? 'border-red-500/50 text-red-400' 
                                : 'border-amber-500/50 text-amber-400'
                            }`}
                          >
                            {issue.severity === 'HIGH' ? '⚠️ Alta' : '⚡ Media'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs text-cyan-400 hover:text-cyan-300"
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                // Get full diagnosis
                                const diagResponse = await axios.get(`${API}/admin/client/${issue.client_id}/diagnose-progress`);
                                const d = diagResponse.data;
                                
                                if (window.confirm(
                                  `¿Corregir automáticamente a mesociclo ${d.diagnosis.expected_mesocycle}?\n\n` +
                                  `Cliente: ${issue.client_name}\n` +
                                  `Actual: Meso ${d.stored_progress?.current_mesocycle}\n` +
                                  `Esperado: Meso ${d.diagnosis.expected_mesocycle}`
                                )) {
                                  const planId = d.plan_id;
                                  await axios.post(`${API}/admin/client/${issue.client_id}/set-progress`, {
                                    plan_id: planId,
                                    mesocycle: d.diagnosis.expected_mesocycle,
                                    week: d.stored_progress?.current_week || 1,
                                    session: d.stored_progress?.current_session || 1,
                                    sessions_completed: d.stored_progress?.sessions_completed || 0
                                  });
                                  toast.success(`✅ ${issue.client_name}: Mesociclo corregido`);
                                  fetchDataHealth();
                                }
                              } catch (error) {
                                toast.error('Error al corregir');
                              }
                            }}
                          >
                            <Stethoscope className="h-3 w-3 mr-1" />
                            Reparar
                          </Button>
                        </div>
                      </div>
                    </Link>
                  ))}
                  {dataHealth.issues.length > 5 && (
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      +{dataHealth.issues.length - 5} clientes más con problemas
                    </p>
                  )}
                </div>
              )}

              {/* All healthy message */}
              {dataHealth.issues && dataHealth.issues.length === 0 && (
                <div className="flex items-center justify-center gap-2 py-4 text-emerald-400">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="text-sm font-medium">Todos los clientes sincronizados correctamente</span>
                </div>
              )}
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{t('quickActions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {quickActions.map((action) => (
                <Link key={action.label} to={action.link}>
                  <Button 
                    variant="outline" 
                    className="w-full h-auto py-4 flex items-center justify-between border-border hover:border-primary/50 hover:bg-primary/5"
                    data-testid={`quick-action-${action.label}`}
                  >
                    <div className="flex items-center gap-3">
                      <action.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                      <span>{action.label}</span>
                    </div>
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Reset All Clients Progress */}
        <Card className="bg-red-500/5 border-red-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-sm bg-red-500/10">
                  <RotateCcw className="h-6 w-6 text-red-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    Resetear Progreso de Clientes
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Todos los clientes volverán a ver la Sesión 1 de su plan asignado
                  </p>
                </div>
              </div>
              <Button 
                onClick={resetAllClientsProgress}
                disabled={resettingClients}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
                data-testid="reset-all-clients-button"
              >
                {resettingClients ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reseteando...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Resetear Todos
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sync Exercises Card */}
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-sm bg-amber-500/10">
                  <Dumbbell className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    Sincronizar Ejercicios
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Actualiza los comentarios técnicos y elimina videos incorrectos de los ejercicios
                  </p>
                </div>
              </div>
              <Button 
                onClick={syncExercises}
                disabled={syncing || syncDone}
                className={syncDone ? "bg-green-600 hover:bg-green-600" : "bg-amber-600 hover:bg-amber-700"}
                data-testid="sync-exercises-button"
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sincronizando...
                  </>
                ) : syncDone ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    ¡Listo!
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sincronizar
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* REORGANIZAR LIBRERÍA DE EJERCICIOS */}
        <Card className="bg-cyan-500/5 border-cyan-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-sm bg-cyan-500/10">
                  <Dumbbell className="h-6 w-6 text-cyan-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    🔧 Reorganizar Librería de Ejercicios
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Normaliza nombres, asigna grupos musculares automáticamente y elimina duplicados
                  </p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  if (!window.confirm('⚠️ Esto reorganizará TODA la librería de ejercicios:\n\n• Normalizar nombres\n• Asignar grupos musculares\n• Eliminar duplicados\n\n¿Continuar?')) {
                    return;
                  }
                  try {
                    toast.info('Reorganizando... esto puede tardar unos segundos');
                    const response = await axios.post(`${API}/admin/reorganize-exercises`);
                    const data = response.data;
                    toast.success(`✅ ${data.message}\n\nEjercicios: ${data.stats.total_original} → ${data.stats.total_final}\nDuplicados eliminados: ${data.stats.duplicates_removed}`);
                    fetchStats();
                  } catch (error) {
                    toast.error('Error al reorganizar ejercicios');
                    console.error(error);
                  }
                }}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="reorganize-exercises-button"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reorganizar Todo
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Create Senior Plan Card */}
        <Card className="bg-emerald-500/5 border-emerald-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-sm bg-emerald-500/10">
                  <ClipboardList className="h-6 w-6 text-emerald-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    Crear Plan Senior
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Crea el plan "Senior - Macro Salud" con el Mesociclo 1 completo (5 semanas, 10 sesiones)
                  </p>
                </div>
              </div>
              <Button 
                onClick={createSeniorPlan}
                disabled={creatingSenior || seniorCreated}
                className={seniorCreated ? "bg-green-600 hover:bg-green-600" : "bg-emerald-600 hover:bg-emerald-700"}
                data-testid="create-senior-button"
              >
                {creatingSenior ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : seniorCreated ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    ¡Creado!
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Plan
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fix Plan IDs Card */}
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-sm bg-blue-500/10">
                  <RefreshCw className="h-6 w-6 text-blue-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    Arreglar IDs de Planes
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Asigna IDs a las sesiones de planes que no los tienen (necesario para guardar entrenamientos)
                  </p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  try {
                    const response = await axios.post(`${API}/admin/fix-plan-ids`);
                    toast.success(`✅ ${response.data.message}`);
                  } catch (error) {
                    toast.error('Error al arreglar planes');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="fix-plan-ids-button"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Arreglar Planes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Create Salud Plan Card */}
        <Card className="bg-pink-500/5 border-pink-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-sm bg-pink-500/10">
                  <Heart className="h-6 w-6 text-pink-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    Crear Plan Salud
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Basado en Senior pero con más intensidad. 4 series, 12 reps, cardio 15min.
                  </p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  try {
                    const response = await axios.post(`${API}/admin/create-salud-plan`);
                    toast.success(`✅ ${response.data.message}`);
                    fetchStats();
                  } catch (error) {
                    toast.error('Error al crear plan Salud');
                  }
                }}
                className="bg-pink-600 hover:bg-pink-700"
                data-testid="create-salud-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Plan Salud
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Create Weider Standard 3 Plan Card */}
        <Card className="bg-purple-500/5 border-purple-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-sm bg-purple-500/10">
                  <Dumbbell className="h-6 w-6 text-purple-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    Crear Plan Weider Standard 3
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Día A: Pecho+Bíceps+Abdomen | Día B: Hombros+Piernas | Día C: Dorsal+Core. 3x10, 30s.
                  </p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  try {
                    const response = await axios.post(`${API}/admin/create-weider-standard-3`);
                    toast.success(`✅ ${response.data.message}`);
                    fetchStats();
                  } catch (error) {
                    toast.error('Error al crear plan Weider Standard 3');
                  }
                }}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="create-weider-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Weider 3
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Create All Tips Card */}
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-sm bg-amber-500/10">
                  <Lightbulb className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    Crear las 3 Píldoras
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Calentamiento, RPE y Escápulas. ⚠️ Borra las existentes y crea las 3 nuevas.
                  </p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  try {
                    const response = await axios.post(`${API}/admin/create-initial-tip`);
                    toast.success(`✅ ${response.data.message}`);
                  } catch (error) {
                    toast.error('Error al crear píldoras');
                  }
                }}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="create-tips-button"
              >
                <Lightbulb className="h-4 w-4 mr-2" />
                Crear 3 Píldoras
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Getting Started Guide */}
        <Card className="bg-card border-border border-primary/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-sm bg-primary/10">
                <ClipboardList className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">
                  {stats.total_clients === 0 ? (
                    language => language === 'es' ? '¡Comienza agregando tu primer cliente!' : 'Start by adding your first client!'
                  ) : (
                    language => language === 'es' ? 'Todo listo para trabajar' : 'Ready to work'
                  )}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {stats.total_clients === 0 
                    ? (language => language === 'es' ? 'Crea clientes, ejercicios y planes para comenzar.' : 'Create clients, exercises and plans to get started.')
                    : (language => language === 'es' ? 'Gestiona tus clientes y planes de entrenamiento.' : 'Manage your clients and training plans.')
                  }
                </p>
              </div>
              <Link to="/admin/clients">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="get-started-button">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
